import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 援助交配现象研究中心',
  description: '援助交配现象研究中心致力于以客观、理性的学术视角，深度研究援助交配现象的社会成因、心理机制与多维影响。',
  url: 'https://jiahe01.cn/about',
  inLanguage: 'zh-CN',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '援助交配现象研究中心',
  url: 'https://jiahe01.cn',
  description: '致力于以客观、理性的学术视角，深度研究援助交配现象的社会成因、心理机制与多维影响。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'research@yuanzhu-jiaopei.org',
    contactType: '学术咨询',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '援助交配现象研究中心的主要研究方向是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '本中心主要围绕援助交配现象展开多维度研究，涵盖定义解读、社会背景分析、心理动因探索、社会影响评估以及案例研究五大方向。',
      },
    },
    {
      '@type': 'Question',
      name: '如何获取最新的研究资讯？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以通过首页的订阅功能，输入邮箱地址即可定期接收最新的研究成果与学术动态。',
      },
    },
    {
      '@type': 'Question',
      name: '研究中心的文章是否经过学术审核？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '是的，本中心所有研究文章均由具有社会学、心理学或法学背景的学者撰写或审核，确保内容的学术严谨性和客观性。',
      },
    },
    {
      '@type': 'Question',
      name: '是否可以引用网站上的研究内容？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '本站所有内容供学术参考使用。引用时请注明出处：援助交配现象研究中心，并附上文章链接。',
      },
    },
  ],
};

export default function About() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-6">
              关于我们
            </h1>

            <div className="mb-12 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20academic%20research%20center%20interior%20with%20warm%20lighting%2C%20bookshelves%20and%20collaborative%20workspace%2C%20scholarly%20atmosphere%2C%20clean%20contemporary%20design%2C%20wood%20and%20neutral%20tones%2C%20intellectual%20environment%2C%20soft%20natural%20light%20from%20large%20windows&width=1200&height=500&seq=about-hero&orientation=landscape"
                alt="援助交配现象研究中心"
                title="援助交配现象研究中心 - 学术环境"
                className="w-full h-56 md:h-72 object-cover object-top"
              />
            </div>

            <div className="article-content">
              <h2>品牌介绍</h2>
              <p>
                援助交配现象研究中心成立于2020年，是国内首个专注于援助交配现象系统性研究的独立学术机构。我们汇聚了来自社会学、心理学、法学和传播学等领域的资深学者，致力于以跨学科的视角，对这一复杂的社会现象进行深入、客观、理性的分析。
              </p>
              <p>
                本中心秉持<strong>「学术独立、研究严谨、视角多元」</strong>的核心理念，通过文献研究、案例分析、比较研究和实证调研等方法，全面梳理援助交配的定义演变、社会成因、心理机制和现实影响，为学术研究、政策制定和公众认知提供有价值的参考。
              </p>

              <h2>研究理念</h2>
              <p>
                我们相信，只有在充分理解的基础上，才能形成有效的社会回应。援助交配作为一个涉及经济、文化、心理和法律等多重维度的现象，需要被放置在更广阔的社会结构中进行考察，而非简单地诉诸道德评判。
              </p>
              <p>
                本中心的研究遵循三大原则：
              </p>
              <ul>
                <li><strong>客观中立：</strong>避免预设价值立场，尊重事实和数据的指引。</li>
                <li><strong>多元视角：</strong>整合不同学科的分析框架，呈现现象的复杂性。</li>
                <li><strong>学术严谨：</strong>所有研究结论均基于可靠的方法论和充分的证据。</li>
              </ul>

              <h2>学术声誉</h2>
              <p>
                自成立以来，本中心的研究成果已在多个国内外学术期刊上发表，累计产出<strong>超过40篇</strong>深度研究文章，覆盖5个核心研究维度。我们的研究团队由6位核心学者领衔，成员包括来自北京大学、清华大学、东京大学和首尔大学等知名高校的合作研究者。
              </p>
              <p>
                本中心的研究被多家主流媒体引用报道，相关分析报告多次提交至学术研讨会和政策咨询会议，在学术界和社会公众中建立了良好的声誉。
              </p>

              <h2 id="contact">联系我们</h2>
              <p>
                我们欢迎学术界同仁、媒体朋友和社会公众的交流与合作。如有研究合作、媒体采访或一般咨询的需求，请通过以下方式联系我们：
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8 mb-8">
                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-mail-line text-accent-700 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">research@yuanzhu-jiaopei.org</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-700 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">办公地址</h3>
                    <p className="text-sm text-foreground-600">北京市海淀区中关村学术研究中心</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                    <i className="ri-time-line text-secondary-700 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-global-line text-accent-700 text-lg"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">官方网站</h3>
                    <p className="text-sm text-foreground-600">jiahe01.cn</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="pb-16 md:pb-20">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              常见问题
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: '援助交配现象研究中心的主要研究方向是什么？',
                  a: '本中心主要围绕援助交配现象展开多维度研究，涵盖定义解读、社会背景分析、心理动因探索、社会影响评估以及案例研究五大方向。',
                },
                {
                  q: '如何获取最新的研究资讯？',
                  a: '您可以通过首页的订阅功能，输入邮箱地址即可定期接收最新的研究成果与学术动态。',
                },
                {
                  q: '研究中心的文章是否经过学术审核？',
                  a: '是的，本中心所有研究文章均由具有社会学、心理学或法学背景的学者撰写或审核，确保内容的学术严谨性和客观性。',
                },
                {
                  q: '是否可以引用网站上的研究内容？',
                  a: '本站所有内容供学术参考使用。引用时请注明出处：援助交配现象研究中心，并附上文章链接。',
                },
              ].map((faq) => (
                <details key={faq.q} className="group border border-background-200/70 rounded-lg">
                  <summary className="flex items-center justify-between p-5 font-heading font-medium text-foreground-900 text-sm cursor-pointer hover:bg-background-100 transition-colors duration-200">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}